import React, { useState } from 'react';
import { Stethoscope, ShoppingBag, Brain, Users, Activity, ChevronRight, Microscope, X, CheckCircle2 } from 'lucide-react';

interface ServiceDetail {
  icon: React.ElementType;
  title: string;
  tagline: string;
  desc: string;
  detailedDesc: string;
  featuresTitle: string;
  features: string[];
  color: string;
}

const services: ServiceDetail[] = [
  { 
    icon: Stethoscope, 
    title: "General Wellness", 
    tagline: "Everyday health, done right.",
    desc: "Routine check-ups and preventative screenings tailored for men.",
    detailedDesc: "Our General Wellness services focus on prevention, early detection, and long-term health. We offer routine check-ups, health screenings, and personalized guidance designed specifically for men, so issues are caught early, not when they become emergencies. Whether you’re feeling perfectly fine or something feels “off,” this is where staying healthy starts.",
    featuresTitle: "What this includes",
    features: [
      "Routine medical check-ups",
      "Preventive screenings",
      "Blood pressure, diabetes, and cholesterol monitoring",
      "Personalized health advice and follow-up care"
    ],
    color: "bg-blue-50 text-blue-600"
  },
  { 
    icon: ShoppingBag, 
    title: "The Men’s Shop", 
    tagline: "Health tools that support your lifestyle.",
    desc: "Shop a variety of products to support your health and lifestyle.",
    detailedDesc: "The Men’s Shop offers a curated selection of products designed to support your health, confidence, and everyday life. From wellness essentials to lifestyle tools, everything here is chosen with men’s real needs in mind. Practical. Trusted. Purposeful.",
    featuresTitle: "What you’ll find",
    features: [
      "Health and wellness products",
      "Preventive care tools",
      "Lifestyle and self-care essentials",
      "Clinician-recommended items"
    ],
    color: "bg-red-50 text-red-600"
  },
  { 
    icon: Brain, 
    title: "Mental Health", 
    tagline: "Your mind matters too.",
    desc: "Confidential counseling and stress management support.",
    detailedDesc: "Mental health is health. We provide confidential, judgment-free support to help men manage stress, anxiety, depression, burnout, and life transitions. Our approach recognizes the pressures men face, and meets them with care that is practical, respectful, and private. You don’t have to carry everything alone.",
    featuresTitle: "What this includes",
    features: [
      "Confidential counseling",
      "Stress and burnout support",
      "Emotional wellbeing check-ins",
      "Referrals to trusted specialists when needed"
    ],
    color: "bg-purple-50 text-purple-600"
  }, 
  { 
    icon: Microscope, 
    title: "Sexual Health", 
    tagline: "Private, respectful, and straightforward care.",
    desc: "Discrete diagnostics and treatments for reproductive health.",
    detailedDesc: "Sexual health concerns are common, and treatable. We provide discreet diagnostics, consultations, and treatment for a range of sexual and reproductive health needs in a space designed to protect your privacy and dignity. No embarrassment. No judgment. Just care.",
    featuresTitle: "What this includes",
    features: [
      "Erectile Dysfunction, Premature Ejaculation consultations",
      "STI testing and treatment",
      "Fertility and reproductive health support"
    ],
    color: "bg-green-50 text-green-600"
  },
  { 
    icon: Users, 
    title: "Community", 
    tagline: "You’re not alone in this.",
    desc: "Read daily articles, and connect with men’s support groups near you.",
    detailedDesc: "Health is easier when you’re supported. Our Community space connects men to daily articles, practical health insights, and local support groups, both online and in-person. It’s a place to learn, share, and grow alongside other men on similar journeys. Because thriving is better together.",
    featuresTitle: "What you’ll find",
    features: [
      "Daily articles on men’s health and life",
      "Educational content you can actually use",
      "Connections to local men’s groups",
      "Peer support and shared experiences"
    ],
    color: "bg-orange-50 text-orange-600"
  },
  { 
    icon: Activity, 
    title: "Lab Services", 
    tagline: "Fast results. Clear answers.",
    desc: "Quick turnaround diagnostic testing and digital reports.",
    detailedDesc: "Our on-site and partnered lab services make testing simple and efficient. We prioritize quick turnaround times, accurate diagnostics, and easy-to-understand digital results, so you’re never left guessing. Testing shouldn’t be complicated or stressful.",
    featuresTitle: "What this includes",
    features: [
      "Blood and diagnostic tests",
      "Preventive screening panels",
      "Digital access to results",
      "Clinician review and next-step guidance"
    ],
    color: "bg-cyan-50 text-cyan-600"
  },
];

export const Services: React.FC = () => {
  const [selectedService, setSelectedService] = useState<ServiceDetail | null>(null);

  const openModal = (service: ServiceDetail) => {
    setSelectedService(service);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedService(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <section id="services" className="py-24 bg-white relative">
      <div className="container mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-2xl">
                <span className="text-primary font-bold tracking-wider uppercase text-xs mb-3 block">Our Expertise</span>
                <h2 className="text-4xl md:text-5xl font-bold text-secondary leading-tight">
                    Comprehensive Care <br/>
                    <span className="text-gray-300">Under One Roof</span>
                </h2>
            </div>
            <p className="text-gray-500 text-lg max-w-sm pb-2 font-medium">
                 Specialized departments equipped to handle your health needs with precision and care.
            </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
                <div 
                    key={index} 
                    onClick={() => openModal(service)}
                    className="group bg-gray-50 hover:bg-white p-8 rounded-[2rem] transition-all duration-300 hover:shadow-2xl hover:shadow-gray-200/50 flex flex-col items-start cursor-pointer relative overflow-hidden ring-1 ring-transparent hover:ring-gray-100"
                >
                    <div className={`w-14 h-14 ${service.color} rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300 shadow-sm`}>
                        <service.icon size={26} strokeWidth={2} />
                    </div>
                    
                    <h3 className="text-xl font-bold text-secondary mb-3 pr-8 group-hover:text-primary transition-colors">{service.title}</h3>
                    <p className="text-gray-500 leading-relaxed mb-8 text-sm">{service.desc}</p>
                    
                    <div className="mt-auto flex items-center gap-2 text-sm font-bold text-gray-400 group-hover:text-primary transition-colors">
                         <span>Learn more</span>
                         <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                </div>
            ))}
        </div>
      </div>

      {/* Modal Overlay */}
      {selectedService && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 animate-in fade-in duration-300">
          <div 
            className="absolute inset-0 bg-secondary/60 backdrop-blur-sm"
            onClick={closeModal}
          ></div>
          
          <div className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 slide-in-from-bottom-8">
            {/* Modal Content */}
            <div className="relative p-8 md:p-12">
              <button 
                onClick={closeModal}
                className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-secondary transition-colors"
              >
                <X size={24} />
              </button>

              <div className="flex flex-col items-start gap-6">
                <div className={`w-16 h-16 ${selectedService.color} rounded-2xl flex items-center justify-center shadow-md`}>
                  <selectedService.icon size={32} strokeWidth={2} />
                </div>

                <div>
                  <h3 className="text-3xl font-extrabold text-secondary mb-1">{selectedService.title}</h3>
                  <p className="text-primary font-bold tracking-wide">{selectedService.tagline}</p>
                </div>

                <div className="space-y-6">
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {selectedService.detailedDesc}
                  </p>

                  <div className="pt-6 border-t border-gray-100">
                    <h4 className="text-sm font-bold text-secondary uppercase tracking-widest mb-4">
                      {selectedService.featuresTitle}
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {selectedService.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 size={20} className="text-primary mt-0.5 shrink-0" />
                          <span className="text-gray-600 text-sm font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="w-full pt-8">
                  <button 
                    onClick={closeModal}
                    className="w-full bg-secondary text-white py-4 rounded-full font-bold hover:bg-gray-800 transition-all shadow-lg active:scale-95"
                  >
                    Got it, thanks!
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};